import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { toast } from '@/lib/toast';

export interface GeneratedDescription {
	html: string;
	title_suggestion: string;
	summary: string;
	bullet_points: string[];
	condition_notes: string;
}

export function useEbayDescription(itemId: string | undefined) {
	const { user } = useAuth();
	const queryClient = useQueryClient();

	const stored = useQuery({
		queryKey: ['ebay-description', itemId, user?.id],
		enabled: !!itemId && !!user?.id,
		staleTime: 5 * 60 * 1000,
		refetchOnWindowFocus: false,
		queryFn: async () => {
			const { data, error } = await (supabase as any)
				.from('user_items')
				.select('ebay_description_html,ebay_description_source')
				.eq('id', itemId)
				.maybeSingle();
			if (error) throw error;
			return data as { ebay_description_html: string | null; ebay_description_source: string | null } | null;
		}
	});

	const generate = useMutation({
		mutationFn: async (): Promise<GeneratedDescription> => {
			if (!itemId) throw new Error('Missing item_id');
			const { data, error } = await supabase.functions.invoke('ebay-generate-description', {
				body: { item_id: itemId }
			});
			if (error) {
				const ctx = (error as any)?.context;
				if (ctx instanceof Response) {
					const body = await ctx.json().catch(() => null);
					throw new Error(body?.error || error.message);
				}
				throw error;
			}
			if ((data as any)?.error) throw new Error((data as any).error);
			return data as GeneratedDescription;
		},
		onError: (err: any) => {
			const msg = String(err?.message || err);
			if (msg === 'rate_limited') {
				toast.error('AI is busy — try again in a moment.');
			} else if (msg === 'credits_exhausted') {
				toast.error('AI credits exhausted. Add credits in workspace settings.');
			} else {
				toast.error(`Could not generate: ${msg}`);
			}
		}
	});

	const save = useMutation({
		mutationFn: async ({ html, source }: { html: string; source: 'ai' | 'manual' }) => {
			if (!itemId) throw new Error('Missing item_id');
			const { error } = await (supabase as any)
				.from('user_items')
				.update({
					ebay_description_html: html || null,
					ebay_description_source: html ? source : null
				})
				.eq('id', itemId);
			if (error) throw error;
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ['ebay-description', itemId] });
			toast.success('Description saved');
		},
		onError: (err: any) => {
			toast.error(`Could not save: ${err?.message || err}`);
		}
	});

	return { stored, generate, save };
}
